// Durable storage for PWA: writes token to FOUR places to maximize survival
// after an app-kill on iOS/Android WebView / Home-Screen PWAs:
//   1. Cookie (10-year expiry, path=/) — most reliable on iOS.
//   2. IndexedDB (survives app-kill on iOS Safari PWAs).
//   3. localStorage (fastest, fallback).
//   4. Cache API (survives iOS PWA storage eviction better than localStorage).
// Also requests `navigator.storage.persist()` so iOS won't auto-clear us.
// On boot, hydrateStorage() reads all four and unifies into a synchronous mirror.

const DB_NAME = "myexp_auth";
const STORE = "kv";
const CACHE = "myexp-auth-v1";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 3650; // 10 years
const mirror = {};

function openDB() {
  return new Promise((resolve) => {
    if (!("indexedDB" in window)) { resolve(null); return; }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });
}
async function idbGet(key) {
  const db = await openDB(); if (!db) return null;
  return new Promise((r) => { const tx = db.transaction(STORE, "readonly").objectStore(STORE).get(key); tx.onsuccess = () => r(tx.result ?? null); tx.onerror = () => r(null); });
}
async function idbSet(key, value) {
  const db = await openDB(); if (!db) return;
  const tx = db.transaction(STORE, "readwrite"); tx.objectStore(STORE).put(value, key);
}
async function idbDel(key) {
  const db = await openDB(); if (!db) return;
  const tx = db.transaction(STORE, "readwrite"); tx.objectStore(STORE).delete(key);
}

function cookieGet(k) {
  const m = document.cookie.match(new RegExp("(?:^|; )" + encodeURIComponent(k) + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}
function cookieSet(k, v) {
  try { document.cookie = `${encodeURIComponent(k)}=${encodeURIComponent(v)}; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax`; } catch { /* ignore */ }
}
function cookieDel(k) {
  try { document.cookie = `${encodeURIComponent(k)}=; Path=/; Max-Age=0; SameSite=Lax`; } catch { /* ignore */ }
}

export async function hydrateStorage() {
  try { if (navigator.storage?.persist) await navigator.storage.persist(); } catch { /* ignore */ }
  const keys = ["token", "auth_user_v1"];
  for (const k of keys) {
    const v = cookieGet(k) || (await idbGet(k)) || localStorage.getItem(k) || (await cacheGet(k));
    if (v) { mirror[k] = v; try { localStorage.setItem(k, v); } catch { /* ignore */ } cookieSet(k, v); idbSet(k, v); cacheSet(k, v); }
  }
}

async function cacheGet(k) {
  try { const c = await caches.open(CACHE); const r = await c.match("/__kv/" + k); return r ? await r.text() : null; } catch { return null; }
}
async function cacheSet(k, v) {
  try { const c = await caches.open(CACHE); await c.put("/__kv/" + k, new Response(v)); } catch { /* ignore */ }
}
async function cacheDel(k) {
  try { const c = await caches.open(CACHE); await c.delete("/__kv/" + k); } catch { /* ignore */ }
}

export function getItem(key) {
  if (mirror[key] !== undefined) return mirror[key];
  return cookieGet(key) || (typeof localStorage !== "undefined" ? localStorage.getItem(key) : null);
}
export function setItem(key, value) {
  mirror[key] = value;
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
  cookieSet(key, value); idbSet(key, value).catch(() => {}); cacheSet(key, value).catch(() => {});
}
export function removeItem(key) {
  delete mirror[key];
  try { localStorage.removeItem(key); } catch { /* ignore */ }
  cookieDel(key); idbDel(key).catch(() => {}); cacheDel(key).catch(() => {});
}
